import { PkBatch, PkItem } from 'src/features/stock-movements/StockMovementsTypes'
import { PkBatchTable } from '../PkBatchTable'
import { PkModal } from '../PkModal/PkModal'
import * as S from './PkModalGondola.styles'
import { useEffect, useState } from 'react'
import Suffix from '../Suffix'
import { useForm } from 'react-hook-form'
import { cloneDeep } from 'lodash'
import ConfirmOrRejectModal from 'src/features/base/components/ConfirmOrRejectModal'
import { useDispatch } from 'react-redux'
import { actions } from 'src/features/stock-movements/StockMovementsState'
import { useSelector } from 'src/utils/store'
import { handleOnKeyDown } from 'src/utils/Form'

interface Props {
  show: boolean
  item: PkItem
  buttonDidNotFitsAction: (item: PkItem) => any
  buttonFitsAction: () => any
  buttonCloseAction: () => any
  testId?: string
}

interface ModalButtonsProps {
  buttonDidNotFitsText: string
  buttonFitsText: string
}

enum ModalTypes {
  INITIAL,
  DID_NOT_FIT_START,
  DID_NOT_FIT_FINAL
}

interface ModalForm {
  quantity: number
  boxQuantity: number
  single: number
  total: number
}

export const PkModalGondola = ({
  show, item, buttonDidNotFitsAction, buttonFitsAction, buttonCloseAction, testId,
}: Props) => {
  const stockMovements = useSelector((state) => state.stockMovements)
  const {
    pkModalDisableButtons,
  } = stockMovements

  const dispatch = useDispatch()

  const color = '#000'

  const [modalType, setModalType] = useState<ModalTypes>(ModalTypes.INITIAL)
  const [modalButtons, setModalButtons] = useState<ModalButtonsProps>()

  const totalQuantity = item.batches.reduce((sum, batch) => sum + batch.quantity, 0)

  const [leftBatches, setLeftBatches] = useState<PkBatch[]>()

  const [showModalConfirmation, setShowModalConfirmation] = useState(false)

  const {
    register,
    formState: { errors },
    getValues,
    trigger,
    setValue,
    setError,
    watch,
    clearErrors,
  } = useForm<ModalForm>()

  const clearState = () => {
    setValue('quantity', 0)
    setValue('boxQuantity', 0)
    setValue('single', 0)
    clearErrors()
  }

  useEffect(() => {
    setModalType(ModalTypes.INITIAL)
    clearState()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show])

  const removeMovementedBatchesQuantities = (_item: PkItem, _quantityToRemove: number): PkItem => {
    const updatedItem = cloneDeep(_item)
    let quantityToRemove = _quantityToRemove
    updatedItem.batches.forEach((batch) => {
      if (batch.quantity >= quantityToRemove) {
        batch.quantity -= quantityToRemove
        quantityToRemove = 0
      } else {
        quantityToRemove -= batch.quantity
        batch.quantity = 0
      }
    })
    updatedItem.batches = updatedItem.batches.filter((batch) => batch.quantity > 0)
    return updatedItem
  }

  useEffect(() => {
    if (modalType === ModalTypes.INITIAL) {
      const initialModalButtons: ModalButtonsProps = {
        buttonDidNotFitsText: 'Não coube',
        buttonFitsText: 'Coube tudo',
      }
      setModalButtons(initialModalButtons)
    } else if (modalType === ModalTypes.DID_NOT_FIT_START || modalType === ModalTypes.DID_NOT_FIT_FINAL) {
      const didNotFitsModal: ModalButtonsProps = {
        buttonDidNotFitsText: 'VOLTAR',
        buttonFitsText: modalType === ModalTypes.DID_NOT_FIT_START ? 'CONTINUAR' : 'CONFIRMAR',
      }
      setModalButtons(didNotFitsModal)
    }
  }, [modalType])

  const batchesDidNotFits = () => {
    if (modalType === ModalTypes.INITIAL) {
      setModalType(ModalTypes.DID_NOT_FIT_START)
      clearState()
    } else if (modalType === ModalTypes.DID_NOT_FIT_START) {
      setModalType(ModalTypes.INITIAL)
    } else if (modalType === ModalTypes.DID_NOT_FIT_FINAL) {
      setModalType(ModalTypes.DID_NOT_FIT_START)
    }
  }

  const onConfirm = async () => {
    if (pkModalDisableButtons) return

    if (modalType === ModalTypes.INITIAL) {
      if (showModalConfirmation) {
        dispatch(actions.updateMovementsField({ key: 'pkModalDisableButtons', value: true }))
        setShowModalConfirmation(false)
        buttonFitsAction()
      } else {
        setShowModalConfirmation(true)
      }
    } else if (modalType === ModalTypes.DID_NOT_FIT_START) {
      const validationBoxQuantitySuccess = await trigger('boxQuantity')
      const validationQuantitySuccess = await trigger('quantity')
      const validationSingleSuccess = await trigger('single')
      if (!validationBoxQuantitySuccess || !validationQuantitySuccess || !validationSingleSuccess) {
        return
      }
      if (getValues('total') > totalQuantity) {
        setError('total', { message: 'Deve ser menor ou igual a quantidade total de produtos' })
        return
      }
      if (getValues('total') < 1) {
        setError('total', { message: 'Deve ser maior que 0 (zero)' })
        return
      }
      const updatedItem = removeMovementedBatchesQuantities(item, totalQuantity - getValues('total'))
      setLeftBatches(updatedItem.batches)
      clearErrors()
      setModalType(ModalTypes.DID_NOT_FIT_FINAL)
    } else if (modalType === ModalTypes.DID_NOT_FIT_FINAL) {
      if (!showModalConfirmation) {
        setShowModalConfirmation(true)
      } else if (leftBatches) {
        dispatch(actions.updateMovementsField({ key: 'pkModalDisableButtons', value: true }))
        setShowModalConfirmation(false)
        const updatedItem = cloneDeep(item)
        updatedItem.batches = leftBatches
        buttonDidNotFitsAction(updatedItem)
      }
    }
  }

  useEffect(() => {
    const quantity = Number(getValues('quantity'))
    const boxQuantity = Number(getValues('boxQuantity'))
    const single = Number(getValues('single'))
    const total = (quantity * boxQuantity) + single
    setValue('total', Number.isNaN(total) ? 0 : total)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watch('quantity'), watch('boxQuantity'), watch('single')])

  return (
    <>
      <ConfirmOrRejectModal
        show={showModalConfirmation}
        title={modalType === ModalTypes.INITIAL ? 'DIRETO GÔNDOLA' : 'NÃO COUBE - DIRETO GÔNDOLA'}
        headerBackgroundColor={color}
        dataTestId='modal-confirmation'
        customizableDescription={(
          <S.ConfirmModalContainer>
            <S.ConfirmModalText>
              {modalType === ModalTypes.INITIAL
                ? 'Ao confirmar a ação, você garante que todas as unidades foram devidamente movidas.'
                : 'Ao confirmar a ação, você garante que as respectivas validades e quantidades restantes foram checadas. As unidades restantes deverão ser movidas para uma nova posição.'}
            </S.ConfirmModalText>
            <S.ConfirmModalConfirmationText>
              Deseja prosseguir?
            </S.ConfirmModalConfirmationText>
          </S.ConfirmModalContainer>
        )}
        onClose={() => setShowModalConfirmation(false)}
        onReject={() => setShowModalConfirmation(false)}
        onConfirm={onConfirm}
      />
      <PkModal
        show={show}
        backgroundColor={color}
        title={modalType === ModalTypes.INITIAL ? 'DIRETO GÔNDOLA' : 'NÃO COUBE - DIRETO GÔNDOLA'}
        testId={testId}
        zindex={showModalConfirmation ? 520 : 1040}
        body={
        (
          <S.Container>
            <S.AddressBox color={color}>
              {item.bestPosition.address}
            </S.AddressBox>
            {modalType === ModalTypes.INITIAL ? (
              <>
                <S.Info color={color}>
                  REPONHA O PRODUTO:
                </S.Info>
                <S.ProductInfoBox color={color}>
                  {item.productDescription.toUpperCase()}
                </S.ProductInfoBox>
                <S.BatchesQuantity color={color}>
                  {totalQuantity} UNIDADES
                </S.BatchesQuantity>
                <PkBatchTable batches={item.batches} color={color} />
              </>
            ) : (
              <>
                <S.ProductInfoBox color={color}>
                  {item.productDescription.toUpperCase()}
                </S.ProductInfoBox>
                <S.BatchesQuantity color={color}>
                  {totalQuantity} UNIDADES
                </S.BatchesQuantity>
                <S.Info color={color}>
                  QUANTAS UNIDADES DO PRODUTO SOBRARAM?
                </S.Info>
                <S.ItemContainer>
                  <Suffix suffix='UN'>
                    <S.Input
                      onKeyDown={(e) => handleOnKeyDown(e, 'quantity')}
                      label='Quantidade de Caixas'
                      name='boxQuantity'
                      id='boxQuantity'
                      type='number'
                      defaultValue={0}
                      disabled={modalType === ModalTypes.DID_NOT_FIT_FINAL}
                      inputProps={{
                        ...register('boxQuantity',
                          {
                            required: 'Campo obrigatório',
                            min: { value: 0, message: 'Deve ser zero ou um valor positivo' },
                            pattern: { value: new RegExp('^[0-9]*$'), message: 'Deve ser um número inteiro' },
                          }),
                        'data-testid': 'pk-modal-gondola-boxQuantity',
                      }}
                      error={!!errors.boxQuantity}
                      helperText={errors.boxQuantity?.message}
                      autoComplete='off'
                    />
                  </Suffix>
                  <Suffix suffix='UN'>
                    <S.Input
                      onKeyDown={(e) => handleOnKeyDown(e, 'single')}
                      label='Caixaria'
                      name='quantity'
                      id='quantity'
                      type='number'
                      defaultValue={0}
                      disabled={modalType === ModalTypes.DID_NOT_FIT_FINAL}
                      inputProps={{
                        ...register('quantity',
                          {
                            required: 'Campo obrigatório',
                            min: { value: 0, message: 'Deve ser zero ou um valor positivo' },
                            pattern: { value: new RegExp('^[0-9]*$'), message: 'Deve ser um número inteiro' },
                          }),
                        'data-testid': 'pk-modal-gondola-quantity',
                      }}
                      error={!!errors.quantity}
                      helperText={errors.quantity?.message}
                      autoComplete='off'
                    />
                  </Suffix>
                </S.ItemContainer>
                <S.ItemContainer>
                  <Suffix suffix='UN'>
                    <S.Input
                      onKeyDown={(e) => handleOnKeyDown(e, 'boxQuantity')}
                      label='Avulsos'
                      name='single'
                      id='single'
                      type='number'
                      defaultValue={0}
                      disabled={modalType === ModalTypes.DID_NOT_FIT_FINAL}
                      inputProps={{
                        ...register('single',
                          {
                            required: 'Campo obrigatório',
                            min: { value: 0, message: 'Deve ser zero ou um valor positivo' },
                            pattern: { value: new RegExp('^[0-9]*$'), message: 'Deve ser um número inteiro' },
                          }),
                        'data-testid': 'pk-modal-gondola-single',
                      }}
                      error={!!errors.single}
                      helperText={errors.single?.message}
                      autoComplete='off'
                    />
                  </Suffix>
                  <S.Input
                    onKeyPress={() => null}
                    label='Total'
                    name='total'
                    id='total'
                    inputProps={{ ...register('total'), 'data-testid': 'pk-modal-gondola-total' }}
                    InputLabelProps={{ shrink: true }}
                    error={!!errors.total}
                    helperText={errors.total?.message}
                    disabled
                  />
                </S.ItemContainer>
                {
                  modalType === ModalTypes.DID_NOT_FIT_FINAL
                  && (
                    <>
                      <S.Info>
                        Confirme se as validades das caixas que sobraram são:
                      </S.Info>
                      {leftBatches && (<PkBatchTable batches={leftBatches} color={color} />)}
                    </>
                  )
                }
              </>
            )}
          </S.Container>
        )
      }
        cancelButtonText={modalButtons?.buttonDidNotFitsText}
        cancelButtonColor='#FF8F1F'
        cancelButtonHoverColor='#BB6815'
        confirmButtonText={modalButtons?.buttonFitsText}
        confirmButtonColor={modalType === ModalTypes.DID_NOT_FIT_FINAL ? '#1983DA' : '#00B578'}
        confirmButtonHoverColor={modalType === ModalTypes.DID_NOT_FIT_FINAL ? '#125992' : '#018055'}
        onReject={batchesDidNotFits}
        onConfirm={onConfirm}
        onClose={buttonCloseAction}
      />
    </>
  )
}
